import {ChangeDetectionStrategy, ChangeDetectorRef, Component} from '@angular/core';
import {WorkspacePanelsMenuService} from '../panels-menu/workspace-panels-menu.service';
import {WorkspaceSecurityContextService} from '../workspace-security-context.service';
import {WorkspacePanels} from '../panels-menu/workspace-panels';
import {PanelAliases, PanelAliasesService} from './panel-aliases.service';
import {DetectMethodChanges, DetectSetterChanges} from '../../utils';

/**
 * CR-20260928-002 — the aliases editor. One row per panel type the user may open (the add-panel
 * menu's own predicate), full name as the label, the alias in a text box. Save stores the whole
 * set as one user setting through `PanelAliasesService`; Cancel discards. Opened from Application
 * Settings as a second popup above it; it saves on its own — the settings' Save/Cancel never touch it.
 */
interface AliasRow {
   /** `WorkspacePanels` enum NAME — the storage key. */
   typeName: string;
   fullName: string;
   alias: string;
}

@Component({
   selector: 'ets-panel-aliases-dialog',
   templateUrl: './panel-aliases-dialog.component.html',
   styleUrls: ['./panel-aliases-dialog.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
   providers: [WorkspacePanelsMenuService, WorkspaceSecurityContextService]
})
export class PanelAliasesDialogComponent {

   constructor(
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _panelsMenu: WorkspacePanelsMenuService,
      private readonly _aliases: PanelAliasesService
   ) {
   }

   private _isVisible = false;
   get isVisible(): boolean {
      return this._isVisible;
   }
   @DetectSetterChanges()
   set isVisible(v: boolean) {
      this._isVisible = v;
   }

   rows: AliasRow[] = [];


   @DetectMethodChanges()
   show(): void {
      this.rows = this.buildRows();
      this.isVisible = true;
   }

   @DetectMethodChanges()
   onShowing(): void {
      this.rows = this.buildRows();
   }

   onAliasChanged(row: AliasRow, ev: any): void {
      row.alias = typeof ev.value === 'string' ? ev.value : '';
   }

   @DetectMethodChanges()
   async save(): Promise<void> {
      const next: PanelAliases = {};
      this.rows.forEach(r => {
         const v = (r.alias || '').trim();
         if (v) {
            next[r.typeName] = v;
         }
      });
      await this._aliases.save(next);
      this.isVisible = false;
   }

   @DetectMethodChanges()
   cancel(): void {
      this.isVisible = false;
   }

   @DetectMethodChanges()
   onHidden(): void {
      this.isVisible = false;
      this.rows = [];
   }

   private buildRows(): AliasRow[] {
      const stored = this._aliases.aliases;
      return this._panelsMenu.getMenu().items
         .filter(mi => mi.isAvailable())
         .map(mi => {
            const typeName = WorkspacePanels[Number(mi.id)];
            const row: AliasRow = {
               typeName,
               fullName: mi.header,
               alias: stored[typeName] || ''
            };
            return row;
         })
         .sort((a, b) => a.fullName.localeCompare(b.fullName));
   }
}
